/**
 * Canonical development profiles for billing plans that are no longer sold.
 *
 * A legacy plan has two distinct representations in the billing app:
 * `currentPrice` identifies the subscribed price, while `currentSiteLimits`
 * describes the allowances currently applied to the site. Keeping both in one
 * profile prevents a legacy price from accidentally being paired with current
 * plan allowances.
 */

const LEGACY_OFFICIAL_THEMES = [
    'alto',
    'casper',
    'dawn',
    'dope',
    'ease',
    'edge',
    'edition',
    'editorial',
    'london',
    'massively',
    'ruby',
    'wave',
    'journal',
    'digest',
    'bulletin',
];

const UNRESTRICTED_PLAN_LIMITS = {
    customThemes: { limit: null },
    limitStripeConnect: { limit: null },
    limitAnalytics: { limit: null },
};

/**
 * These allowances mirror the established legacy billing fixtures. They model
 * legacy billing plans, not old Ghost versions.
 *
 * `membersPriceLimit` is price-level data for the synthetic `currentPrice`.
 * `siteLimits` is the applied allowance returned by `get-current-site`.
 *
 * @type {object.<string, {
 *   productName: string,
 *   membersPriceLimit: number,
 *   siteLimits: object,
 *   omittedSiteLimits: string[]
 * }>}
 */
export const LEGACY_PLAN_PROFILES = {
    'legacy-starter': {
        productName: 'Starter',
        membersPriceLimit: 500,
        siteLimits: {
            staff: { limit: 1 },
            members: { limit: 500 },
            newsletters: { limit: 1 },
            customThemes: { limit: LEGACY_OFFICIAL_THEMES },
        },
        omittedSiteLimits: ['customIntegrations', 'limitStripeConnect', 'limitAnalytics'],
    },
    'legacy-creator': {
        productName: 'Creator',
        membersPriceLimit: 1000,
        siteLimits: {
            staff: { limit: 2 },
            members: { limit: 1000 },
            newsletters: { limit: 3 },
            ...UNRESTRICTED_PLAN_LIMITS,
        },
        omittedSiteLimits: ['customIntegrations'],
    },
    'legacy-team': {
        productName: 'Team',
        membersPriceLimit: 1000,
        siteLimits: {
            staff: { limit: 5 },
            members: { limit: 1000 },
            newsletters: { limit: null },
            ...UNRESTRICTED_PLAN_LIMITS,
        },
        omittedSiteLimits: ['customIntegrations'],
    },
    'legacy-business': {
        productName: 'Business',
        membersPriceLimit: 500,
        siteLimits: {
            staff: { limit: null },
            members: { limit: 500 },
            newsletters: { limit: null },
            ...UNRESTRICTED_PLAN_LIMITS,
        },
        omittedSiteLimits: ['customIntegrations'],
    },
    'legacy-basic': {
        productName: 'Basic',
        membersPriceLimit: 1000,
        siteLimits: {
            staff: { limit: 2 },
            members: { limit: 1000 },
            newsletters: { limit: 1 },
            ...UNRESTRICTED_PLAN_LIMITS,
        },
        omittedSiteLimits: ['customIntegrations'],
    },
};

export const LEGACY_PLAN_OVERRIDE_IDS = Object.keys(LEGACY_PLAN_PROFILES);

const DEFAULT_SITE_LIMIT_VALUES = {
    staff: { total: 1 },
    members: { total: 0 },
    newsletters: { total: 1 },
    customThemes: { active: 'source', hasOfficialTheme: true },
    customIntegrations: { active: false },
    limitStripeConnect: { active: false },
    limitAnalytics: { active: false },
};

/**
 * @param {string|null|undefined} id
 * @returns {boolean}
 */
export function isLegacyPlanOverride(id) {
    return Boolean(id && LEGACY_PLAN_PROFILES[id]);
}

/**
 * @param {string|null|undefined} id
 * @returns {object|null}
 */
export function getLegacyPlanProfile(id) {
    return isLegacyPlanOverride(id) ? LEGACY_PLAN_PROFILES[id] : null;
}

/**
 * @param {string} limitName
 * @param {object} limit
 * @returns {boolean}
 */
function hasExceededAllowance(limitName, limit) {
    if (['staff', 'members', 'newsletters'].includes(limitName)) {
        return typeof limit.limit === 'number' && limit.total > limit.limit;
    }

    if (limitName === 'customThemes') {
        if (!Array.isArray(limit.limit)) {
            return false;
        }
        return limit.hasOfficialTheme === false;
    }

    return limit.active === true && limit.limit === true;
}

/**
 * Applies the selected legacy plan's allowances while preserving the site's
 * actual usage. This function is intentionally independent of browser state;
 * the browser adapter decides which profile is active and when to apply it.
 *
 * @param {object} currentSiteResponse
 * @param {string|null|undefined} overrideId
 * @returns {object}
 */
export function applyLegacyPlanSiteLimits(currentSiteResponse, overrideId) {
    const profile = getLegacyPlanProfile(overrideId);
    if (!profile || !currentSiteResponse?.currentSiteLimits) {
        return currentSiteResponse;
    }

    const currentSiteLimits = { ...currentSiteResponse.currentSiteLimits };

    profile.omittedSiteLimits.forEach((limitName) => {
        delete currentSiteLimits[limitName];
    });

    Object.entries(profile.siteLimits).forEach(([limitName, allowance]) => {
        const defaults = DEFAULT_SITE_LIMIT_VALUES[limitName] || {};
        const currentLimit = currentSiteLimits[limitName] || {};
        const emulatedLimit = { ...defaults, ...currentLimit, ...allowance };

        if (limitName === 'members') {
            delete emulatedLimit.members_override;
        }

        emulatedLimit.exceeded = hasExceededAllowance(limitName, emulatedLimit);
        currentSiteLimits[limitName] = emulatedLimit;
    });

    return { ...currentSiteResponse, currentSiteLimits };
}

/**
 * Creates the legacy `currentPrice` representation consumed by the plan
 * selector. Existing display-only price fields are retained when available;
 * identity and limit fields always come from the selected legacy profile.
 *
 * @param {object|null} currentPrice
 * @param {string|null|undefined} overrideId
 * @returns {object|null}
 */
export function createLegacyCurrentPrice(currentPrice, overrideId) {
    const profile = getLegacyPlanProfile(overrideId);
    if (!profile) {
        return currentPrice;
    }

    return {
        cost: 0,
        billing_period: 'year',
        features: [],
        ...currentPrice,
        active: 1,
        nickname: overrideId,
        group: '2021',
        stripe_price_id: `dev-plan-override-${overrideId}`,
        base_product: profile.productName,
        legacy: true,
        limits: [{ name: 'members', value: profile.membersPriceLimit }],
    };
}
